import React, {
  CSSProperties,
  Fragment,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router';

import Button, { FollowButton } from '~/components/ui/Button';
import Loading from '~/components/Loading';
import FormattedMessage from '~/components/common/FormattedMessage';

import config from '~/config';

import Avatar from '../Avatar';
import Markdown from '../Markdown';
import styles from './UserOverview.scss';

interface Thumb {
  id: string;
  is_video: boolean;
  is_flash: boolean;
  image: {
    vthumb: {
      url: string;
    };
    thumb: {
      url: string;
    };
  };
}

const UserOverview = ({
  children,
  user,
  style,
}: {
  children?: ReactNode;
  user: {
    id?: number;
    username: string;
    avatar: {
      url?: string;
    };
    gallery_description?: string;
  };
  style?: CSSProperties;
}) => {
  const [isAnon, setAnon] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [thumbs, setThumbs] = useState<Thumb[]>([]);

  const renderThumbs = () => {
    return thumbs.map((thumb) => {
      const thumbUrl = thumb.is_flash
        ? '/img/piczelflashth.png'
        : thumb.image[thumb.is_video ? 'vthumb' : 'thumb'].url;

      return (
        <div
          className={styles.UserOverview__Thumb}
          style={{ backgroundImage: `url(${thumbUrl})` }}
          key={thumb.id}
        />
      );
    });
  };

  const loadUserData = async () => {
    setLoading(true);

    try {
      const response = await fetch(
        `${config.api}/users/${user.username}/gallery?limit=4`
      );
      const json = await response.json();

      if (json.status && json.status === 'error') {
        setAnon(true);
        return;
      }

      setThumbs(json.data);
    } catch (error) {
      setAnon(true);
      console.error(`Error while loading UserOverview: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserData();
  }, []);

  return (
    <div className={styles.UserOverview} style={style}>
      <b className={styles.UserOverview__Username}>{user.username}</b>
      <FollowButton username={user.username} small />
      <Avatar
        userId={user.id}
        username={user.username}
        className={styles.UserOverview__Avatar}
      />
      <div className={styles.UserOverview__Description}>
        <Markdown>{user.gallery_description}</Markdown>
      </div>
      {isLoading ? (
        <Loading size="small" />
      ) : (
        <Link
          to={`/gallery/${user.username}`}
          className={styles.UserOverview__Thumbs}
        >
          {renderThumbs()}
        </Link>
      )}
      <div className={styles.UserOverview__Actions}>
        {!isAnon && (
          <Fragment>
            <Button to={`/watch/${user.username}`} color="black">
              <FormattedMessage
                id="StreamUserOverview"
                defaultMessage="Stream"
              />
            </Button>
            <Button to={`/gallery/${user.username}`} color="black">
              <FormattedMessage
                id="GalleryUserOverview"
                defaultMessage="Gallery"
              />
            </Button>
          </Fragment>
        )}

        {children &&
          React.cloneElement(children as React.ReactElement<any>, { isAnon })}
      </div>
    </div>
  );
};

export const useUserOverview = () => {
  const [show, setShow] = useState(false);
  const [user, setUser] = useState(null);

  return {
    show,
    setShow,
    user,
    setUser,
    UserOverview: !user || !show ? null : <UserOverview user={user} />,
  };
};

export default UserOverview;
